import React, { useMemo } from "react";
import {
  useLoadingState,
  useLoadingError,
  useLoadingController,
} from "@contexts/Loading.context";
import {
  requestStateError,
  requestStateLoading,
} from "@ps-bridge/models/brands/requestState";
import { unwrap as unwrapErrorMessageText } from "@ps-bridge/models/brands/errorMessageText";
import { unwrap as unwrapErrorInfoText } from "@ps-bridge/models/brands/errorInfoText";
import { useIoEffectOption } from "@hooks/effects/useIoEffect";
import { ioOptionNone, ioOptionSome } from "@hooks/effects/useIoEffectWithCleanup";
import * as Option from "@utils/Option";
import type { IOts, OptionTs } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";
import LoadingOverlayPanel, {
  type LoadingOverlayPhase,
  type SpinnerVariant,
} from "./panels/LoadingOverlay.panel";

type LoadingOverlayProps = {
  spinnerVariant: SpinnerVariant;
};

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ spinnerVariant }) => {
  const state = useLoadingState();
  const { message, info } = useLoadingError();
  const { setIdle } = useLoadingController();
  const errorMessage = unwrapErrorMessageText(message);
  const errorInfo: OptionTs<string> = Option.fold(
    info,
    () => Option.none<string>(),
    (value) => Option.some(unwrapErrorInfoText(value)),
  );

  const phase: LoadingOverlayPhase =
    state === requestStateLoading
      ? "loading"
      : state === requestStateError
        ? "error"
        : "idle";

  const dismissOnEmptyError: OptionTs<IOts<Ok>> = useMemo(
    () =>
      state === requestStateError && errorMessage.trim().length === 0
        ? ioOptionSome(setIdle)
        : ioOptionNone(),
    [errorMessage, state, setIdle],
  );

  useIoEffectOption(dismissOnEmptyError, [dismissOnEmptyError]);

  return (
    <LoadingOverlayPanel
      phase={phase}
      loading={{ spinnerVariant }}
      error={{
        message: errorMessage,
        info: errorInfo,
        onDismiss: setIdle,
      }}
    />
  );
};

export default LoadingOverlay;
